import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../services/api";
import Constants from "../../services/constants";
import { hideLoader, showLoader } from "./loaderSlice";
import { showToast } from "./toastSlice";
import { logoutAction } from "./authSlice";
import {
  setActiveOrg,
  setOrgDetail,
  setActiveOrgPlan,
} from "./organizationSlice";
import { setSubscribedPlan } from "./planSlice";

const initialState = {
  data: null,
  roles: [],
  userRole: {},
  profileSetup: {
    loading: false,
    error: null,
    currentStep: 1,
    completed: false,
  },
  paymentMethods: {
    data: [],
    loading: false,
    error: null,
  },
  skipOrgId: null,
  country_name: "India", // Default country
};

// Get profile setup status
export const getProfileStatusAction = createAsyncThunk(
  "user/profile_status",
  async (_, { dispatch }) => {
    try {
      const res = await api("get", Constants.END_POINT.profile_setup_status);
      if (res?.success && res?.data) {
        return res.data;
      } else {
        throw new Error(res.message || "Failed to get profile status");
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    }
  }
);

// Update profile step 1
export const updateProfileStep1Action = createAsyncThunk(
  "user/profile_step1",
  async (data, { dispatch, getState }) => {
    try {
      dispatch(showLoader());
      // Ensure country details are always included
      const state = getState();
      const defaultCountryData = {
        country_code: state.user.data?.country_code,
        country_name: state.user.country_name || state.user.data?.country_name || "India",
        country_countryCode: state.user.data?.country_countryCode,
      };

      // Merge the incoming data with default country data
      const payloadData = {
        ...data,
        country_code: data.country_code || defaultCountryData.country_code,
        country_name: data.country_name || defaultCountryData.country_name,
        country_countryCode:
          data.country_countryCode || defaultCountryData.country_countryCode,
      };

      const res = await api(
        "post",
        Constants.END_POINT.profile_setup_step1,
        payloadData,
        { "X-Country": payloadData.country_name },
        {}
      );

      if (res?.success && res?.data) {
        return res.data;
      } else {
        throw new Error(res.message || "Failed to update profile");
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  }
);

// Update profile step 2
export const updateProfileStep2Action = createAsyncThunk(
  "user/profile_step2",
  async (data, { dispatch }) => {
    try {
      dispatch(showLoader());
      const res = await api(
        "post",
        Constants.END_POINT.profile_setup_step2,
        data
      );
      if (res?.success && res?.data) {
        return res.data;
      } else {
        throw new Error(
          res.message || "Failed to update professional information"
        );
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  }
);

// Update profile step 3
export const updateProfileStep3Action = createAsyncThunk(
  "user/profile_step3",
  async (data, { dispatch, getState }) => {
    try {
      dispatch(showLoader());
      // Get country_name from Redux state
      const state = getState();
      // Use country_name from state directly, fallback to data.country_name, then default to India
      const country_name = state.user.country_name || state.user.data?.country_name || "India";
      const res = await api(
        "post",
        Constants.END_POINT.profile_setup_step3,
        data,
        { "X-Country": country_name },
        {}
      );

      if (res?.success && res?.data) {
        return res.data;
      } else {
        throw new Error(res.message || "Failed to setup organization");
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  }
);

// Complete profile setup
export const completeProfileSetupAction = createAsyncThunk(
  "user/complete_profile",
  async (data = {}, { dispatch, getState }) => {
    try {
      dispatch(showLoader());
      // Get country_name from Redux state
      const state = getState();
      // Use country_name from state directly, fallback to data.country_name, then default to India
      const country_name = state.user.country_name || state.user.data?.country_name || "India";
      const res = await api(
        "post",
        Constants.END_POINT.profile_setup_complete,
        data,
        { "X-Country": country_name },
        {}
      );
      if (res?.success && res?.data) {
        localStorage.removeItem("inviteStatus");
        // Removed success toast notification
        return res.data;
      } else {
        throw new Error(res.message || "Failed to complete profile setup");
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  }
);

// Skip current step
export const skipProfileStepAction = createAsyncThunk(
  "user/skip_step",
  async (_, { dispatch, getState }) => {
    try {
      dispatch(showLoader());
      // Get country_name from Redux state
      const state = getState();
      // Use country_name from state directly, fallback to data.country_name, then default to India
      const country_name = state.user.country_name || state.user.data?.country_name || "India";
      const res = await api(
        "post",
        Constants.END_POINT.profile_setup_skip,
        null,
        { "X-Country": country_name },
        {}
      );

      if (res?.success && res?.data) {
        dispatch(setSkipOrgId(res?.data?.organization_id));
        return res.data;
      } else {
        throw new Error(res.message || "Failed to skip step");
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  }
);

// Get my profile
export const getMyProfileAction = createAsyncThunk(
  "user/my_profile",
  async ({ navigate }, { dispatch, getState }) => {
    let userData = null;
    try {
      const res = await api("get", Constants.END_POINT.my_profile, {});
      const activeOrg = getState()?.org?.active;
      if (res?.success && res?.data) {
        dispatch(setUserDetail(res?.data));
        if (res?.data?.Organizations?.length) {
          dispatch(setOrgDetail(res?.data?.Organizations));
          if (!activeOrg) {
            dispatch(setActiveOrg(res?.data?.Organizations[0]));
            dispatch(
              setActiveOrgPlan(
                res?.data?.Organizations[0]?.Subscriptions[0]?.Plan
              )
            );
            dispatch(
              setSubscribedPlan({
                subscription: res?.data?.Organizations[0]?.Subscriptions[0],
              })
            );
          } else {
            const org = res.data.Organizations.find(
              (sub) => sub?.id === activeOrg?.id
            );
            dispatch(setActiveOrgPlan(org?.Subscriptions[0]?.Plan));
            dispatch(
              setSubscribedPlan({ subscription: org?.Subscriptions[0] })
            );
          }
        }

        userData = res?.data;
      } else if (res?.data?.status === 404) {
        dispatch(
          showToast({
            severity: "error",
            summary: "Session closed, please login again to continue",
          })
        );
        dispatch(logoutAction({ navigate }));
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
    } finally {
      return Promise.resolve(userData);
    }
  }
);

// Update my profile
export const updateMyProfileAction = createAsyncThunk(
  "user/update_profile",
  async ({ data, next }, { dispatch }) => {
    try {
      dispatch(showLoader());
      const res = await api("post", Constants.END_POINT.update_profile, data);
      if (res?.success && res?.data) {
        dispatch(setUserDetail(res?.data));
        if (next) next();
      } else {
        throw new Error(res.message || "Failed to update profile");
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  }
);

// Update user role
export const updateUserRoleAction = createAsyncThunk(
  "user/update_role",
  async ({ id, data, next }, { dispatch }) => {
    try {
      dispatch(showLoader());
      const res = await api("post", Constants.END_POINT.update_role + id, data);
      if (res?.success && res?.data) {
        dispatch(showToast({ severity: "success", summary: res.message }));
        if (next) next();
      } else {
        throw new Error(res.message || "Failed to update role");
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  }
);

// Get all roles
export const getAllRolesAction = createAsyncThunk(
  "user/get_roles",
  async ({ navigate }, { dispatch }) => {
    try {
      const res = await api("get", Constants.END_POINT.user_roles);
      if (res?.success && res?.data) {
        dispatch(setUserRoles(res?.data));
        return res.data;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        localStorage.removeItem("authToken");
        navigate("/");
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    }
  }
);

export const getMyRole = createAsyncThunk(
  "user/my_role",
  async (orgId, { dispatch }) => {
    try {
      if (!orgId) {
        throw new Error("Organization ID is required");
      }
      const res = await api(
        "get",
        Constants.END_POINT.my_role + `?id=${orgId}`,
        {}
      );
      if (res?.success && res?.data?.Role) {
        const role = res.data.Role;
        if (role.name) {
          role.name = role.name.toLowerCase();
        }

        dispatch(setRole(role));
        const showSuccessToast = false;
        if (showSuccessToast) {
          dispatch(
            showToast({
              severity: "success",
              summary: res?.message,
            })
          );
        }

        return role;
      } else {
        throw new Error(res.message || "Failed to get user role");
      }
    } catch (error) {
      console.error("Error getting role:", error);
      dispatch(
        showToast({
          severity: "error",
          summary: error.message || "Failed to get user role",
        })
      );
      throw error;
    }
  }
);

// List payment methods
export const listPaymentMethodsAction = createAsyncThunk(
  "user/listPaymentMethods",
  async ({ orgId }, { dispatch }) => {
    try {
      const res = await api(
        "get",
        `${Constants.END_POINT.getPaymentMethods}/${orgId}`
      );
      if (res?.success && res?.data) {
        return res.data;
      } else {
        throw new Error(res.message || "Failed to fetch payment methods");
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    }
  }
);

// Set default payment method
export const setDefaultPaymentMethodAction = createAsyncThunk(
  "user/setDefaultPaymentMethod",
  async (cardId, { dispatch }) => {
    try {
      dispatch(showLoader());
      const res = await api(
        "post",
        `${Constants.END_POINT.setDefaultPaymentMethod}/${cardId}`
      );
      if (res?.success && res?.data) {
        dispatch(
          showToast({
            severity: "success",
            summary: "Default payment method updated successfully",
          })
        );
        return res.data;
      } else {
        throw new Error(res.message || "Failed to set default payment method");
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  }
);

// Delete payment method
export const deletePaymentMethodAction = createAsyncThunk(
  "user/deletePaymentMethod",
  async (cardId, { dispatch }) => {
    try {
      dispatch(showLoader());
      const res = await api(
        "delete",
        `${Constants.END_POINT.deletePaymentMethod}/${cardId}`
      );
      if (res?.success) {
        dispatch(
          showToast({
            severity: "success",
            summary: "Payment method deleted successfully",
          })
        );
        return cardId;
      } else {
        throw new Error(res.message || "Failed to delete payment method");
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUserDetail: (state, action) => {
      state.data = action.payload;
    },
    setUserRoles: (state, action) => {
      state.roles = action.payload;
    },
    setRole: (state, action) => {
      state.userRole = action.payload;
    },
    setSkipOrgId: (state, action) => {
      state.skipOrgId = action.payload;
    },
    setCountryName: (state, action) => {
      state.country_name = action.payload;
      // Also set in data object if it exists
      if (state.data) {
        state.data.country_name = action.payload;
      }
    },
    resetProfileSetup: (state) => {
      state.profileSetup = initialState.profileSetup;
    },
    setCurrentStep: (state, action) => {
      state.profileSetup.currentStep = action.payload;
    },
    resetUser: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Payment Methods
      .addCase(listPaymentMethodsAction.pending, (state) => {
        state.paymentMethods.loading = true;
        state.paymentMethods.error = null;
      })
      .addCase(listPaymentMethodsAction.fulfilled, (state, action) => {
        state.paymentMethods.loading = false;
        state.paymentMethods.data = action.payload;
      })
      .addCase(listPaymentMethodsAction.rejected, (state, action) => {
        state.paymentMethods.loading = false;
        state.paymentMethods.error = action.error.message;
      })
      .addCase(setDefaultPaymentMethodAction.fulfilled, (state, action) => {
        state.paymentMethods.data = state.paymentMethods.data.map((card) => ({
          ...card,
          is_default: card.id === action.payload.id,
        }));
      })
      .addCase(deletePaymentMethodAction.fulfilled, (state, action) => {
        state.paymentMethods.data = state.paymentMethods.data.filter(
          (card) => card.id !== action.payload
        );
      })
      // Profile Status
      .addCase(getProfileStatusAction.pending, (state) => {
        state.profileSetup.loading = true;
      })
      .addCase(getProfileStatusAction.fulfilled, (state, action) => {
        state.profileSetup.loading = false;
        state.profileSetup.currentStep = action.payload.onboarding_step;
        state.profileSetup.completed = action.payload.profile_completed;
        state.data = { ...state.data, ...action.payload };
      })
      .addCase(getProfileStatusAction.rejected, (state, action) => {
        state.profileSetup.loading = false;
        state.profileSetup.error = action.error.message;
      })
      // Step 1
      .addCase(updateProfileStep1Action.fulfilled, (state, action) => {
        state.profileSetup.currentStep = 2;
        // Get the country data from the original action payload (meta.arg)
        const originalData = action.meta.arg;
        // Merge the API response with the country data
        state.data = {
          ...state.data,
          ...action.payload,
          // Preserve country data that wasn't returned by the API
          country_code: originalData.country_code,
          country_name: originalData.country_name,
          country_countryCode: originalData.country_countryCode,
        };
      })
      // Step 2
      .addCase(updateProfileStep2Action.fulfilled, (state, action) => {
        state.profileSetup.currentStep = 3;
        state.data = { ...state.data, ...action.payload };
      })
      // Step 3
      .addCase(updateProfileStep3Action.fulfilled, (state, action) => {
        state.profileSetup.currentStep = 4;
        state.data = { ...state.data, ...action.payload };
      })
      // Complete Setup
      .addCase(completeProfileSetupAction.fulfilled, (state, action) => {
        state.profileSetup.completed = true;
        state.data = {
          ...state.data,
          ...action.payload,
          profile_completed: true,
        };
      })
      // Skip Step
      .addCase(skipProfileStepAction.fulfilled, (state, action) => {
        state.profileSetup.currentStep = action.payload.onboarding_step;
        state.data = { ...state.data, ...action.payload };
      });
  },
});

export const {
  setUserDetail,
  setUserRoles,
  setRole,
  resetProfileSetup,
  setCurrentStep,
  setSkipOrgId,
  setCountryName,
  resetUser,
} = userSlice.actions;

export default userSlice.reducer;
